// MediaRecorder wrapper with chunked upload + IndexedDB local backup.
// Each chunk is POSTed as it's emitted by MediaRecorder. After Stop, we finalize.

import api, { getAccessToken, refreshAccessToken } from "./api";
import { saveChunk, markChunkUploaded } from "./recordingBackup";

const CHUNK_TIMESLICE_MS = 20_000; // 20s per chunk

// Phase 2a — interview duration policy. Soft cap is a UI warning only; hard
// cap auto-stops the MediaRecorder and finalizes the recording. Server
// rejects finalize >120 min as defense in depth.
export const SOFT_LIMIT_SECONDS = 90 * 60;   // 90 min — UI banner
export const HARD_LIMIT_SECONDS = 120 * 60;  // 120 min — auto-stop

// IndexedDB ownership is centralized in recordingBackup.js (the SINGLE module
// that opens the `hireproof_recorder` DB) so the recorder never opens it at a
// stale version. We only mirror chunks here via saveChunk()/markChunkUploaded()
// so a page-death / mid-recording upload failure leaves chunks for Phase
// auth-(b) recovery.

// ---- Chunked uploader -----------------------------------------------------

async function uploadChunk(recordingId, index, blob, retried = false) {
  // Use fetch (not axios) so we can send raw bytes cleanly. The access token
  // lives in JS memory now (Phase 5), so read it from the api module rather
  // than localStorage.
  const url = `${api.defaults.baseURL}/recordings/${recordingId}/chunks?index=${index}`;
  const token = getAccessToken();
  const headers = { "Content-Type": "application/octet-stream" };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const resp = await fetch(url, {
    method: "POST",
    headers,
    body: blob,
    credentials: "include",
  });
  // Access token may expire mid-recording (30-min TTL). The refresh cookie is
  // still valid for days — mint a fresh token and retry ONCE. The chunk is
  // already mirrored to IndexedDB, so even a failed upload stays recoverable.
  if (resp.status === 401 && !retried) {
    const ok = await refreshAccessToken();
    if (ok) return uploadChunk(recordingId, index, blob, true);
  }
  if (!resp.ok) throw new Error(`chunk upload failed: ${resp.status}`);
  return resp.json();
}

// ---- Recorder -------------------------------------------------------------

export class HireproofRecorder {
  constructor({ recordingId, stream, mimeType, onChunkProgress, onError }) {
    this.recordingId = recordingId;
    this.stream = stream;
    this.mimeType = mimeType || pickSupportedMimeType();
    this.onChunkProgress = onChunkProgress || (() => {});
    this.onError = onError || (() => {});
    this.chunkIndex = 0;
    this.uploadedCount = 0;
    this.totalEmitted = 0;
    this.startedAt = null;
    this.totalPausedMs = 0;
    this._pauseStartedAt = null;
    this.queue = Promise.resolve();
    this.recorder = null;
    this.wakeLock = null;
  }

  async start() {
    this.recorder = new MediaRecorder(this.stream, { mimeType: this.mimeType });
    this.recorder.ondataavailable = (evt) => this._handleChunk(evt.data);
    this.recorder.onerror = (evt) => this.onError(evt.error || new Error("recorder error"));
    this.startedAt = Date.now();
    // Wake lock so mobile screen doesn't sleep mid-interview
    try {
      if ("wakeLock" in navigator) {
        this.wakeLock = await navigator.wakeLock.request("screen");
      }
    } catch {
      // best-effort
    }
    this.recorder.start(CHUNK_TIMESLICE_MS);
  }

  pause() {
    if (this.recorder && this.recorder.state === "recording") {
      this.recorder.pause();
      this._pauseStartedAt = Date.now();
    }
  }

  resume() {
    if (this.recorder && this.recorder.state === "paused") {
      this.recorder.resume();
      if (this._pauseStartedAt) {
        this.totalPausedMs += Date.now() - this._pauseStartedAt;
        this._pauseStartedAt = null;
      }
    }
  }

  isPaused() {
    return this.recorder?.state === "paused";
  }

  elapsedSeconds() {
    if (!this.startedAt) return 0;
    const paused = this._pauseStartedAt ? Date.now() - this._pauseStartedAt : 0;
    return Math.max(0, (Date.now() - this.startedAt - this.totalPausedMs - paused) / 1000);
  }

  async stopAndFinalize() {
    if (!this.recorder) return null;
    // Trigger the final dataavailable then close
    const stopPromise = new Promise((resolve) => {
      this.recorder.onstop = () => resolve();
    });
    this.recorder.stop();
    await stopPromise;
    // Drain queued uploads
    await this.queue;
    // Stop tracks
    if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
    if (this.wakeLock) {
      try { await this.wakeLock.release(); } catch { /* ignore */ }
    }
    return {
      duration_seconds: this.elapsedSeconds(),
      mime_type: this.mimeType,
      chunk_count: this.chunkIndex,
    };
  }

  _handleChunk(blob) {
    if (!blob || blob.size === 0) return;
    const index = this.chunkIndex++;
    this.totalEmitted += 1;
    this.onChunkProgress({
      index,
      uploaded: this.uploadedCount,
      emitted: this.totalEmitted,
      bytes: blob.size,
    });
    // Queue uploads serially per-recording to preserve order safety. The chunk
    // is mirrored to IndexedDB BEFORE the upload attempt so a mid-recording
    // upload failure (or page death) still leaves it on-device for recovery;
    // it's marked uploaded only after the server confirms it.
    this.queue = this.queue
      .then(() => saveChunk(this.recordingId, index, blob))
      .then(() => uploadChunk(this.recordingId, index, blob))
      .then(() => markChunkUploaded(this.recordingId, index))
      .then(() => {
        this.uploadedCount += 1;
        this.onChunkProgress({
          index,
          uploaded: this.uploadedCount,
          emitted: this.totalEmitted,
          bytes: blob.size,
        });
      })
      .catch((err) => this.onError(err));
  }
}

export function pickSupportedMimeType() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const t of candidates) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported(t)) return t;
  }
  return "audio/webm";
}

export async function getMicStream() {
  return navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
}

export async function getTabAudioStream({ onShareEnded } = {}) {
  // getDisplayMedia returns video too; we ignore video and only use the audio track.
  const display = await navigator.mediaDevices.getDisplayMedia({
    video: true,
    audio: true,
  });
  const audioTracks = display.getAudioTracks();
  if (audioTracks.length === 0) {
    // Stop everything and throw — user did not share tab audio
    display.getTracks().forEach((t) => t.stop());
    const err = new Error("No tab audio was shared. Please retry and check 'Share tab audio'.");
    err.code = "NO_TAB_AUDIO";
    throw err;
  }
  // When the user clicks the browser's native "Stop sharing" control, the
  // captured tab-audio track fires 'ended'. Surface that to the caller so it
  // can finalize the recording gracefully (preserving everything captured so
  // far) instead of silently recording dead air against a dead source.
  if (typeof onShareEnded === "function") {
    audioTracks[0].addEventListener(
      "ended",
      () => {
        try {
          onShareEnded();
        } catch {
          /* never let the listener throw */
        }
      },
      { once: true }
    );
  }
  // Mix tab audio + mic for online interviews
  let mic;
  try {
    mic = await getMicStream();
  } catch (err) {
    // No mic? Still proceed with tab audio.
    mic = null;
  }
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const dest = ctx.createMediaStreamDestination();
  ctx.createMediaStreamSource(new MediaStream([audioTracks[0]])).connect(dest);
  if (mic) {
    ctx.createMediaStreamSource(mic).connect(dest);
  }
  // Stop the video track immediately (we only kept getDisplayMedia for audio)
  display.getVideoTracks().forEach((t) => t.stop());
  return dest.stream;
}
